import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';

import {mergeMap, of, tap} from 'rxjs';
import {catchError, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {Store} from '@ngrx/store';


import {MessageService, ToastMessageOptions} from "primeng/api";
import {GetMessage} from "../../../global-config/utils/toast-messages.utils";
import {
  addBulkRolePermission, addBulkRolePermissionFailure, addBulkRolePermissionSuccess,
  addRole,
  addRoleFailure, addRolePermission, addRolePermissionFailure, addRolePermissionSuccess,
  addRoleSuccess,
  loadRoles,
  loadRolesFailure,
  loadRolesSuccess,
  modifyRole,
  modifyRoleFailure,
  modifyRolePermission,
  modifyRolePermissionFailure,
  modifyRolePermissionSuccess,
  modifyRoleSuccess, removeBulkRolePermission, removeBulkRolePermissionFailure, removeBulkRolePermissionSuccess,
  removeRole,
  removeRoleFailure, removeRolePermission, removeRolePermissionFailure, removeRolePermissionSuccess,
  removeRoleSuccess,
  setSelectedRole,
  setSelectedRoleFailure,
  setSelectedRoleSuccess
} from "./role.action";
import {RoleService} from "../../data-access/role.service";
import {selectAllRoles, selectRoles} from "./role.selectors";
import {AppState} from "../../../shared/state/app.state";
import {RolePermissionService} from "../../data-access/role-permission.service";

@Injectable()
export class RoleEffects {

  loadRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRoles),
      switchMap(() =>
        this.roleService.get()
          .pipe(
            map((roles) => loadRolesSuccess({roles: roles})),
            catchError((error) => of(loadRolesFailure({ error: GetMessage('LoadFailure', 'Role') })))
          )
      )
    )
  );

  modifyRoles$ = createEffect(() =>
      this.actions$.pipe(
        ofType(modifyRole),
        withLatestFrom(this.store.select(selectRoles)),
        switchMap(([action, roles]) =>
          this.roleService.update(action.role)
            .pipe(
              map((role) => modifyRoleSuccess({role: role, successMessage: GetMessage('ModifySuccess', 'Role')})),
              catchError((error) => of(modifyRoleFailure({ error: GetMessage('ModifyFailure', 'Role') })))
            )
        )
      )
  );

  createRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addRole),
      switchMap((action) =>
        this.roleService.create(action.role)
          .pipe(
            map((addedRole) => addRoleSuccess({role: addedRole, successMessage: GetMessage('AddSuccess', 'Role')})),
            catchError((error) => of(addRoleFailure({ error: GetMessage('AddFailure', 'Role') })))
          )
      )
    )
  );

  removeRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeRole),
      switchMap((action) =>
        this.roleService.remove(action.roleId)
          .pipe(
            map((removedRoleId) => removeRoleSuccess({roleId: removedRoleId, successMessage: GetMessage('RemoveSuccess', 'Role')})),
            catchError((error) => of(removeRoleFailure({ error: GetMessage('RemoveFailure', 'Role')})))
          )
      )
    )
  );

  setSelectedRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSelectedRole),
      switchMap((action) =>
        this.store.select(selectAllRoles)
          .pipe(
            map((roles) => setSelectedRoleSuccess({ selectedRole: roles.find(role => role.id == action.selectedRoleId) ?? null })),
            catchError((error) => of(setSelectedRoleFailure({ error })))
          )
      )
    )
  );

  setSelectedRoleFromAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addRoleSuccess),
      switchMap((action) =>
        this.store.select(selectAllRoles)
          .pipe(
            map((roles) => setSelectedRoleSuccess({ selectedRole: roles.find(role => role.id == action.role.id) ?? null })),
            catchError((error) => of(setSelectedRoleFailure({ error })))
          )
      )
    )
  );

  // Role Permissions

  modifyRolePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyRolePermission),
      withLatestFrom(this.store.select(selectRoles)),
      switchMap(([action, roles]) =>
        this.rolePermissionService.update(action.rolePermission)
          .pipe(
            map((role) => modifyRolePermissionSuccess({rolePermission: role, successMessage: GetMessage('ModifySuccess', 'Role Permission')})),
            catchError((error) => of(modifyRolePermissionFailure({ error: GetMessage('ModifyFailure', 'Permission') })))
          )
      )
    )
  );

  createRolePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addRolePermission),
      mergeMap((action) =>
        this.rolePermissionService.create(action.rolePermission)
          .pipe(
            map((addedRole) => addRolePermissionSuccess({rolePermission: addedRole, successMessage: GetMessage('AddSuccess', 'Role Permission')})),
            catchError((error) => of(addRolePermissionFailure({ error: GetMessage('AddFailure', 'Permission') })))
          )
      )
    )
  );

  createBulkRolePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addBulkRolePermission),
      mergeMap((action) =>
        this.rolePermissionService.createBulk(action.rolePermissions)
          .pipe(
            map((addedRoles) => addBulkRolePermissionSuccess({rolePermissions: addedRoles?.data, successMessage: GetMessage('AddSuccess', 'Role Permission')})),
            catchError((error) => of(addBulkRolePermissionFailure({ error: GetMessage('AddFailure', 'Permission') })))
          )
      )
    )
  );

  removeRolePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeRolePermission),
      withLatestFrom(this.store.select(selectRoles)),
      mergeMap(([action, roles]) =>
        this.rolePermissionService.remove(action.rolePermissionId)
          .pipe(
            map((removedRolePermissionId) => removeRolePermissionSuccess({rolePermissionId: removedRolePermissionId, successMessage: GetMessage('RemoveSuccess', 'Role Permission')})),
            catchError((error) => of(removeRolePermissionFailure({ error: GetMessage('RemoveFailure', 'Permission')})))
          )
      )
    )
  );

  removeBulkRolePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeBulkRolePermission),
      withLatestFrom(this.store.select(selectRoles)),
      mergeMap(([action, roles]) =>
        this.rolePermissionService.removeBulk(action.rolePermissionIds)
          .pipe(
            map((removedRolePermissionIds) => removeBulkRolePermissionSuccess({rolePermissionIds: removedRolePermissionIds, successMessage: GetMessage('RemoveSuccess', 'Role Permission')})),
            catchError((error) => of(removeBulkRolePermissionFailure({ error: GetMessage('RemoveFailure', 'Permission')})))
          )
      )
    )
  );

  toastSuccessMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        addRoleSuccess,
        modifyRoleSuccess,
        removeRoleSuccess,

        addBulkRolePermissionSuccess,
        removeBulkRolePermissionSuccess,
        addRolePermissionSuccess,
        modifyRolePermissionSuccess,
        removeRolePermissionSuccess,
      ),
      tap(({ successMessage }) => {
          return this.messageService.add({
            severity: 'success',
            summary: successMessage,
          } as ToastMessageOptions)
        }
      )
    ),
    { dispatch: false }
  );

  toastErrorMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          addRoleFailure,
          modifyRoleFailure,
          removeRoleFailure,
          loadRolesFailure,

          addBulkRolePermissionFailure,
          removeBulkRolePermissionFailure,
          addRolePermissionFailure,
          modifyRolePermissionFailure,
          removeRolePermissionFailure,
        ),
        tap(({ error }) => {
            return this.messageService.add({
              severity: 'error',
              summary: error,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private messageService: MessageService,
    private roleService: RoleService,
    private rolePermissionService: RolePermissionService,
  ) {}
}

